import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Plus, Trash2, Pencil, X, ShieldCheck } from "lucide-react";
import { toast, Toaster } from "sonner";
import BulkEditBanner, { useBulkEdits, BulkEditToggle } from "../../components/BulkEditBanner";

// Capability catalogue — keeps frontend & backend in sync.
const CAP_INFO = {
    mr: {
        label: "Medical Representative",
        desc: "Place orders on behalf of clients, file Daily Report Sheets, look up customer dues, log market expenses with receipt photos.",
    },
    packing: {
        label: "Packing & Dispatch",
        desc: "See orders received, mark line items as packed or out-of-stock, hand off to accounts for billing.",
    },
    stock: {
        label: "Stock keeper",
        desc: "Daily stock counts, batch numbers, low-stock alerts. Updates are diff-logged for audit.",
    },
    accounts: {
        label: "Accounts & Billing",
        desc: "Generate Tally-style invoices, manage customer master & GSTIN, upload external bills, review MR expenses, see all dues.",
    },
    view: {
        label: "View only",
        desc: "Read-only sidebar items — useful for trainees or auditors.",
    },
};
const CAPS = ["mr", "packing", "stock", "accounts", "view"];
const blank = { name: "", capabilities: [], order_index: 0, active: true };

export default function AdminDesignations() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [f, setF] = useState(blank);
    const [bulkMode, setBulkMode] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);

    const { setCell, cellValue, dirtyIds, dirtyPatches, reset: resetBulk } = useBulkEdits(items);

    const load = () => api.get("/admin/designations").then(r => setItems(r.data));
    useEffect(() => { load(); }, []);

    const toggleCap = (c) => setF((p) => ({
        ...p,
        capabilities: p.capabilities.includes(c)
            ? p.capabilities.filter((x) => x !== c)
            : [...p.capabilities, c],
    }));
    const save = async () => {
        if (!f.name) return toast.error("Name required");
        try {
            editing === "new"
                ? await api.post("/admin/designations", f)
                : await api.put(`/admin/designations/${editing}`, f);
            toast.success("Saved");
            setEditing(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/designations/${id}`); load(); };

    /** Toggle one capability inside the bulk-edit patch for a designation. */
    const toggleCapInline = (d, cap) => {
        const currentList = cellValue(d.id, "capabilities", d.capabilities || []);
        const next = currentList.includes(cap)
            ? currentList.filter((x) => x !== cap)
            : [...currentList, cap];
        // Compare arrays by stringification — small lists, perfectly fine.
        const same = JSON.stringify([...next].sort()) === JSON.stringify([...(d.capabilities || [])].sort());
        setCell(d.id, "capabilities", same ? (d.capabilities || []) : next, d.capabilities || []);
    };

    const saveBulk = async () => {
        const patches = dirtyPatches();
        if (patches.length === 0) return;
        setBulkBusy(true);
        let ok = 0, fail = 0;
        for (const { id, original, patch } of patches) {
            if (!original) continue;
            try {
                const payload = {
                    name: patch.name ?? original.name,
                    capabilities: patch.capabilities ?? (original.capabilities || []),
                    order_index: patch.order_index ?? (original.order_index || 0),
                    active: patch.active !== undefined ? !!patch.active : !!original.active,
                };
                // eslint-disable-next-line no-await-in-loop
                await api.put(`/admin/designations/${id}`, payload);
                ok += 1;
            } catch (e) {
                fail += 1;
                toast.error(`${original.name}: ${e.response?.data?.detail || "Save failed"}`);
            }
        }
        setBulkBusy(false);
        if (ok > 0) toast.success(`Saved · ${ok} designation${ok === 1 ? "" : "s"}${fail ? ` (${fail} failed)` : ""}`);
        resetBulk();
        await load();
    };

    return (
        <div data-testid="admin-designations">
            <Toaster position="top-center" />
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
                <div>
                    <div className="label-eyebrow text-sky-700 flex items-center gap-1"><ShieldCheck className="w-3 h-3" />Role-based access</div>
                    <h1 className="font-serif text-4xl text-stone-900 mt-2">Roles &amp; access</h1>
                    <p className="text-sm text-stone-600 mt-2 max-w-2xl">
                        Each <em>designation</em> bundles capability toggles. Assign a designation to an employee on the <strong>Employees</strong> page; they instantly see only the sidebar items their role allows.
                    </p>
                </div>
                <div className="flex gap-2">
                    <BulkEditToggle active={bulkMode} onClick={() => { setBulkMode(!bulkMode); resetBulk(); }} testId="toggle-bulk-mode" />
                    <Button onClick={() => { setEditing("new"); setF(blank); }} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="add-designation"><Plus className="w-4 h-4 mr-2" />New designation</Button>
                </div>
            </div>

            {bulkMode && (
                <BulkEditBanner
                    dirtyCount={dirtyIds.length}
                    busy={bulkBusy}
                    onReset={resetBulk}
                    onSave={saveBulk}
                    helperText="Rename designations and toggle capability chips inline. Save commits every change at once."
                    testIdPrefix="bulk"
                />
            )}

            {editing && (
                <div className="bg-white border border-stone-200 p-5 mb-6 space-y-4" data-testid="des-form">
                    <div className="flex justify-between"><h2 className="font-serif text-2xl">{editing === "new" ? "New" : "Edit"} designation</h2><Button variant="ghost" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button></div>
                    <div>
                        <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Designation name</div>
                        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Senior MR · Packing Lead · Stock Keeper" className="rounded-none border-stone-300" data-testid="des-name" />
                    </div>
                    <div>
                        <div className="label-eyebrow text-stone-500 mb-3">Access toggles</div>
                        <div className="space-y-2">
                            {CAPS.map((c) => {
                                const on = f.capabilities.includes(c);
                                const info = CAP_INFO[c] || { label: c, desc: "" };
                                return (
                                    <label
                                        key={c}
                                        className={`block border p-3 cursor-pointer transition ${on ? "border-sky-700 bg-sky-50" : "border-stone-300"}`}
                                        data-testid={`cap-toggle-${c}`}
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="font-medium text-sm">{info.label}</div>
                                                <div className="text-xs text-stone-500 mt-1">{info.desc}</div>
                                            </div>
                                            <Switch checked={on} onCheckedChange={() => toggleCap(c)} data-testid={`cap-switch-${c}`} />
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Sort order</div>
                            <Input type="number" value={f.order_index} onChange={(e) => setF({ ...f, order_index: parseInt(e.target.value || 0, 10) })} className="rounded-none border-stone-300" />
                        </div>
                        <label className="flex items-center justify-between border border-stone-300 p-3"><span className="text-sm">Designation is active</span><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /></label>
                    </div>
                    <Button onClick={save} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="des-save">Save designation</Button>
                </div>
            )}

            <div className="bg-white border border-stone-200">
                {items.map((d) => {
                    const isEdited = dirtyIds.includes(d.id);
                    const liveName = cellValue(d.id, "name", d.name);
                    const liveCaps = cellValue(d.id, "capabilities", d.capabilities || []);
                    const liveActive = cellValue(d.id, "active", !!d.active);
                    return (
                        <div key={d.id} className={`border-b border-stone-100 last:border-b-0 p-4 grid grid-cols-12 gap-3 items-center ${isEdited ? "bg-amber-50/40" : ""}`} data-testid={`des-row-${d.id}`}>
                            <div className="col-span-3">
                                {bulkMode ? (
                                    <Input
                                        value={liveName}
                                        onChange={(e) => setCell(d.id, "name", e.target.value, d.name)}
                                        className={`rounded-none h-8 text-sm font-medium ${liveName !== d.name ? "border-amber-500 bg-amber-50" : "border-stone-200"}`}
                                        data-testid={`bulk-name-${d.id}`}
                                    />
                                ) : (
                                    <div className="font-medium">{d.name}{!d.active && <span className="ml-2 text-xs text-stone-400 uppercase tracking-widest">hidden</span>}</div>
                                )}
                            </div>
                            <div className="col-span-7 flex flex-wrap gap-1">
                                {bulkMode ? CAPS.map((c) => {
                                    const on = liveCaps.includes(c);
                                    return (
                                        <button
                                            key={c}
                                            type="button"
                                            onClick={() => toggleCapInline(d, c)}
                                            className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 transition ${on ? "bg-sky-700 text-white border-sky-700" : "border-stone-300 text-stone-500 hover:border-sky-300"}`}
                                            title={CAP_INFO[c]?.desc}
                                            data-testid={`bulk-cap-${d.id}-${c}`}
                                        >
                                            {CAP_INFO[c]?.label || c}
                                        </button>
                                    );
                                }) : (
                                    <>
                                        {(d.capabilities || []).map(c => (
                                            <span key={c} className="text-[10px] uppercase tracking-widest border border-sky-700 text-sky-700 px-2 py-0.5" title={CAP_INFO[c]?.label}>
                                                {CAP_INFO[c]?.label || c}
                                            </span>
                                        ))}
                                        {(d.capabilities || []).length === 0 && <span className="text-xs text-stone-400">no access</span>}
                                    </>
                                )}
                            </div>
                            <div className="col-span-2 flex justify-end gap-1 items-center">
                                {bulkMode && (
                                    <label className="flex items-center gap-1 text-xs text-stone-600 mr-2">
                                        <Switch checked={!!liveActive} onCheckedChange={(v) => setCell(d.id, "active", v, !!d.active)} data-testid={`bulk-active-${d.id}`} />
                                        <span>Active</span>
                                    </label>
                                )}
                                <button onClick={() => { setEditing(d.id); setF({ ...d }); }} className="p-1 hover:bg-stone-100" data-testid={`des-edit-${d.id}`}><Pencil className="w-4 h-4" /></button>
                                <button onClick={() => del(d.id)} className="text-red-700 p-1 hover:bg-red-50" data-testid={`des-del-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                            </div>
                        </div>
                    );
                })}
                {items.length === 0 && <div className="p-8 text-center text-sm text-stone-500">No designations yet — create one for each role you need (MR, Packing, Stock, Accounts…).</div>}
            </div>
        </div>
    );
}
